import { Parser, TC, Ty } from '@cotera/era';
import { ZodUtils } from '@cotera/utilities';
import { Output } from 'ai';
import type { LLMInput } from './llm-input-types';
import { z } from 'zod/v3';

export type StreamTextOutputSchemaEncoding = 'zod' | 'strictJsonSchema';

export type StreamTextOutputFromShape = {
  output: NonNullable<LLMInput['output']>;
  shouldWrapOutput: boolean;
  /** Set by the model adapter's {@link buildZodStreamTextOutputFromShape} vs strict builder. */
  schemaEncoding: StreamTextOutputSchemaEncoding;
};

const resolveOutputShapeValidator = (
  outputShape: Ty.ExtendedAttributeType
): { shouldWrapOutput: boolean; validator: z.ZodTypeAny } => {
  const shouldWrapOutput = TC.isPrimitive(outputShape);
  let validator = Parser.validatorForType(outputShape);
  if (validator.isNullable()) {
    validator = ZodUtils.unwrapZodSchema(validator);
  }
  return { shouldWrapOutput, validator };
};

/**
 * Non-strict path: Zod schemas for {@link Output.object} (Anthropic, Google, and
 * other providers that do not pair with `strictJsonSchema`).
 */
export const buildZodStreamTextOutputFromShape = (
  outputShape: Ty.ExtendedAttributeType
): StreamTextOutputFromShape => {
  const { shouldWrapOutput, validator } =
    resolveOutputShapeValidator(outputShape);
  const schema = shouldWrapOutput ? z.object({ output: validator }) : validator;
  return {
    output: Output.object({ schema }),
    shouldWrapOutput,
    schemaEncoding: 'zod',
  };
};

/** @deprecated Use {@link buildZodStreamTextOutputFromShape}. */
export const buildGoogleStreamTextOutputFromShape =
  buildZodStreamTextOutputFromShape;

/** Unwrap `{ output: value }` for primitive shapes — same semantics as extract-object. */
export const unwrapStructuredStreamOutput = (
  raw: unknown,
  outputShape: Ty.ExtendedAttributeType
): Ty.Scalar => {
  if (TC.isPrimitive(outputShape)) {
    return (raw as { output: Ty.Scalar }).output;
  }
  return raw as Ty.Scalar;
};
